// Reusable blog sidebar — popular posts + newsletter signup
"use client";

import { useEffect, useState, FormEvent } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { TrendingUp, Mail, CheckCircle2 } from "lucide-react";

interface PopularPost {
  id: string;
  slug: string;
  title: string;
  coverImage: string;
  publishedAt: string | null;
  viewCount: number;
}

export default function BlogSidebar() {
  const [popular, setPopular] = useState<PopularPost[]>([]);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/blog/popular?limit=5")
      .then((r) => r.json())
      .then(setPopular)
      .catch(() => {});
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState("loading"); setErr(null);
    const res = await fetch("/api/blog/newsletter", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (res.ok) { setState("ok"); setEmail(""); }
    else {
      const data = await res.json().catch(() => ({}));
      setErr(data?.error || "Coś poszło nie tak");
      setState("err");
    }
  };

  return (
    <aside className="space-y-6">
      {/* Popular posts */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900">Popularne wpisy</h3>
        </div>
        {popular.length === 0 ? (
          <p className="text-sm text-slate-500">Brak danych.</p>
        ) : (
          <ul className="space-y-3">
            {popular.map((p, idx) => (
              <li key={p.id}>
                <Link
                  href={`/blog/${p.slug}`}
                  className="group flex gap-3 items-start"
                >
                  <span className="text-2xl font-bold text-slate-200 leading-none w-7 shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {p.title}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {p.viewCount} {p.viewCount === 1 ? "wyświetlenie" : "wyświetleń"}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Newsletter */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-6 shadow-md"
      >
        <Mail className="w-7 h-7 mb-3 opacity-90" />
        <h3 className="font-bold text-lg leading-tight mb-1">Newsletter</h3>
        <p className="text-sm text-blue-100 mb-4">
          Co tydzień nowe artykuły, porady i nowości. Bez spamu.
        </p>
        {state === "ok" ? (
          <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium">Dziękujemy! Sprawdź pocztę.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="twoj@email.pl"
              className="w-full rounded-lg px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 bg-white outline-none focus:ring-2 focus:ring-yellow-300"
            />
            <button
              type="submit"
              disabled={state === "loading"}
              className="w-full rounded-lg px-3 py-2.5 text-sm font-semibold bg-yellow-300 text-slate-900 hover:bg-yellow-200 transition-colors disabled:opacity-60"
            >
              {state === "loading" ? "Zapisywanie..." : "Zapisz się"}
            </button>
            {err && <p className="text-xs text-red-200">{err}</p>}
          </form>
        )}
      </motion.div>
    </aside>
  );
}
